/**
 * Lottie Animation Lazy Loader (dotLottie対応版)
 * .json と .lottie 両方のファイル形式をサポート
 *
 * 使い方: div要素に data-lottie-src を指定
 * <div class="lottie-animation" data-lottie-src="animation.lottie"></div>
 * <div class="lottie-animation" data-lottie-src="animation.json"></div>
 */
(function() {
  'use strict';

  // デバッグモード
  const DEBUG = false;

  function log(message, data) {
    if (DEBUG) {
      console.log('[LottieLazyLoad] ' + message, data || '');
    }
  }

  // :empty で非表示になるのを防ぐCSS
  const style = document.createElement('style');
  style.textContent = '[data-lottie-src] { display: inline-flex !important; }';
  document.head.appendChild(style);

  // アニメーションインスタンスを保持
  const animations = new WeakMap();

  // Intersection Observer のオプション
  const observerOptions = {
    root: null,
    rootMargin: '100px',
    threshold: 0
  };

  // 画面外判定用（より広い範囲で破棄）
  const destroyObserverOptions = {
    root: null,
    rootMargin: '500px',
    threshold: 0
  };

  // JSONキャッシュ（再フェッチ防止）
  const jsonCache = new Map();

  // ファイル拡張子を取得
  function getFileExtension(url) {
    const path = url.split('?')[0];
    const ext = path.split('.').pop().toLowerCase();
    return ext;
  }

  // JSONを取得（キャッシュ付き）
  function fetchJSON(url) {
    if (jsonCache.has(url)) {
      return Promise.resolve(jsonCache.get(url));
    }
    return fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        jsonCache.set(url, data);
        return data;
      });
  }

  // .lottie ファイルからJSONを抽出
  function fetchDotLottie(url) {
    if (jsonCache.has(url)) {
      return Promise.resolve(jsonCache.get(url));
    }

    log('🔗 .lottieファイル取得開始: ' + url);

    return fetch(url)
      .then(function(res) {
        if (!res.ok) {
          throw new Error('HTTP error: ' + res.status);
        }
        log('📥 .lottieファイル取得成功, Content-Type: ' + res.headers.get('content-type'));
        return res.arrayBuffer();
      })
      .then(function(buffer) {
        log('📦 ArrayBuffer取得, サイズ: ' + buffer.byteLength + ' bytes');
        return unzipDotLottie(buffer);
      })
      .then(function(data) {
        jsonCache.set(url, data);
        return data;
      })
      .catch(function(err) {
        log('❌ .lottie読み込みエラー: ' + err.message);
        throw err;
      });
  }

  // .lottie (ZIP) からアニメーションJSONを抽出
  async function unzipDotLottie(buffer) {
    const zip = new Uint8Array(buffer);

    log('📦 .lottie解析開始, サイズ: ' + zip.length + ' bytes');

    // ZIPファイルのセントラルディレクトリを探す
    let centralDirOffset = -1;
    for (let i = zip.length - 22; i >= 0; i--) {
      if (zip[i] === 0x50 && zip[i+1] === 0x4b && zip[i+2] === 0x05 && zip[i+3] === 0x06) {
        centralDirOffset = zip[i+16] | (zip[i+17] << 8) | (zip[i+18] << 16) | (zip[i+19] << 24);
        break;
      }
    }

    if (centralDirOffset === -1) {
      throw new Error('Invalid ZIP file');
    }

    log('📂 セントラルディレクトリ発見: offset=' + centralDirOffset);

    // animations/xxx.json を探す
    let offset = centralDirOffset;

    while (offset < zip.length) {
      if (zip[offset] !== 0x50 || zip[offset+1] !== 0x4b || zip[offset+2] !== 0x01 || zip[offset+3] !== 0x02) {
        break;
      }

      const compressedSize = zip[offset+20] | (zip[offset+21] << 8) | (zip[offset+22] << 16) | (zip[offset+23] << 24);
      const uncompressedSize = zip[offset+24] | (zip[offset+25] << 8) | (zip[offset+26] << 16) | (zip[offset+27] << 24);
      const fileNameLength = zip[offset+28] | (zip[offset+29] << 8);
      const extraLength = zip[offset+30] | (zip[offset+31] << 8);
      const commentLength = zip[offset+32] | (zip[offset+33] << 8);
      const localHeaderOffset = zip[offset+42] | (zip[offset+43] << 8) | (zip[offset+44] << 16) | (zip[offset+45] << 24);

      const fileName = new TextDecoder().decode(zip.slice(offset + 46, offset + 46 + fileNameLength));
      log('📄 ファイル発見: ' + fileName);

      // .jsonファイルを探す（manifest.json以外）
      if (fileName.endsWith('.json') && fileName !== 'manifest.json') {
        log('🎯 アニメーションJSON発見: ' + fileName);

        // ローカルファイルヘッダーからデータを取得
        const localOffset = localHeaderOffset;
        const localFileNameLength = zip[localOffset+26] | (zip[localOffset+27] << 8);
        const localExtraLength = zip[localOffset+28] | (zip[localOffset+29] << 8);
        const dataStart = localOffset + 30 + localFileNameLength + localExtraLength;
        const compressionMethod = zip[localOffset+8] | (zip[localOffset+9] << 8);

        log('📊 圧縮方式: ' + compressionMethod + ', 圧縮サイズ: ' + compressedSize + ', 解凍サイズ: ' + uncompressedSize);

        let fileData;
        if (compressionMethod === 0) {
          // 無圧縮
          fileData = zip.slice(dataStart, dataStart + uncompressedSize);
        } else if (compressionMethod === 8) {
          // Deflate圧縮
          const compressedData = zip.slice(dataStart, dataStart + compressedSize);
          fileData = await inflateRaw(compressedData);
        }

        if (fileData) {
          const jsonStr = new TextDecoder().decode(fileData);
          log('✅ JSON抽出成功, サイズ: ' + jsonStr.length + ' chars');
          return JSON.parse(jsonStr);
        }
      }

      offset += 46 + fileNameLength + extraLength + commentLength;
    }

    throw new Error('Animation JSON not found in .lottie file');
  }

  // Deflate解凍
  async function inflateRaw(data) {
    // DecompressionStream APIを使用（モダンブラウザ対応）
    if (typeof DecompressionStream !== 'undefined') {
      const ds = new DecompressionStream('deflate-raw');
      const reader = new Blob([data]).stream().pipeThrough(ds).getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      const output = new Uint8Array(totalLength);
      let offset = 0;
      chunks.forEach(chunk => {
        output.set(chunk, offset);
        offset += chunk.length;
      });

      return output;
    }

    // フォールバック: pako使用
    if (typeof pako !== 'undefined') {
      return pako.inflateRaw(data);
    }

    throw new Error('Decompression not supported. Please use a modern browser or include pako library.');
  }

  // ローカルストレージキー（ログインアニメーション用）
  const LOGIN_ANIM_STORAGE_KEY = 'login_lottie_played';
  const LOGIN_ANIM_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1日

  // ログインアニメーションが再生済みかチェック
  function isLoginAnimPlayed() {
    const stored = localStorage.getItem(LOGIN_ANIM_STORAGE_KEY);
    if (!stored) return false;
    const timestamp = parseInt(stored, 10);
    if (isNaN(timestamp)) return false;
    return (Date.now() - timestamp) < LOGIN_ANIM_EXPIRY_MS;
  }

  // ログインアニメーション再生済みを記録
  function markLoginAnimPlayed() {
    localStorage.setItem(LOGIN_ANIM_STORAGE_KEY, Date.now().toString());
  }

  // アニメーションを読み込む
  function loadAnimation(container, src) {
    const ext = getFileExtension(src);
    const fetchFn = ext === 'lottie' ? fetchDotLottie : fetchJSON;
    const isLoginAnim = container.dataset.lottieLogin === 'true';

    log('📥 読み込み開始 (' + ext + ')', src);
    container.dataset.loaded = 'loading';

    fetchFn(src).then(function(animationData) {
      // 既に破棄されていたらスキップ
      if (container.dataset.loaded !== 'loading') return;

      // ログインアニメーションの場合の特別処理
      if (isLoginAnim) {
        const alreadyPlayed = isLoginAnimPlayed();

        const anim = lottie.loadAnimation({
          container: container,
          renderer: 'svg',
          loop: false,
          autoplay: !alreadyPlayed,
          animationData: animationData
        });

        if (alreadyPlayed) {
          // 既に再生済みなら最後のフレームで停止
          anim.goToAndStop(anim.totalFrames - 1, true);
          log('⏸ ログインアニメーション停止状態で表示（再生済み）', src);
        } else {
          // 初回再生：完了時に記録して停止
          anim.addEventListener('complete', function() {
            markLoginAnimPlayed();
            log('✅ ログインアニメーション再生完了、記録済み', src);
          });
          log('▶ ログインアニメーション初回再生', src);
        }

        animations.set(container, anim);
        container.dataset.loaded = 'true';
        return;
      }

      // 通常のアニメーション
      const anim = lottie.loadAnimation({
        container: container,
        renderer: 'svg',
        loop: container.dataset.lottieLoop !== 'false',
        autoplay: true,
        animationData: animationData
      });

      animations.set(container, anim);
      container.dataset.loaded = 'true';
      log('▶ 再生開始', src);
    }).catch(function(err) {
      log('❌ 読み込みエラー', err);
      container.dataset.loaded = '';
    });
  }

  // Lottie要素を監視するObserver（読み込み用）
  const lottieObserver = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      const container = entry.target;
      const src = container.dataset.lottieSrc;

      if (entry.isIntersecting) {
        if (src && !container.dataset.loaded) {
          loadAnimation(container, src);
        }
      }
    });
  }, observerOptions);

  // 画面外で破棄するObserver
  const destroyObserver = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      const container = entry.target;
      const src = container.dataset.lottieSrc;

      if (!entry.isIntersecting && container.dataset.loaded === 'true') {
        const anim = animations.get(container);
        if (anim) {
          anim.destroy();
          animations.delete(container);
          container.innerHTML = '';
          container.dataset.loaded = '';
          log('🗑 破棄（メモリ解放）', src);
        }
      }
    });
  }, destroyObserverOptions);

  // 要素を監視対象に追加
  function observeElement(element) {
    if (element.dataset.observed) return;
    if (!element.dataset.lottieSrc) return;

    lottieObserver.observe(element);
    destroyObserver.observe(element);
    element.dataset.observed = 'true';
    log('👁 監視開始', element.dataset.lottieSrc);
  }

  // 初期化
  function init() {
    if (typeof lottie === 'undefined') {
      log('lottie-web を待機中...');
      setTimeout(init, 100);
      return;
    }

    const elements = document.querySelectorAll('[data-lottie-src]');
    log('初期化: ' + elements.length + '個のLottie要素を検出');

    elements.forEach(observeElement);
  }

  // MutationObserverで動的に追加される要素も監視
  function setupMutationObserver() {
    const mutationObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            if (node.dataset && node.dataset.lottieSrc) {
              observeElement(node);
            }
            const children = node.querySelectorAll && node.querySelectorAll('[data-lottie-src]');
            if (children) {
              children.forEach(observeElement);
            }
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // ページ読み込み完了後に初期化
  if (document.readyState === 'complete') {
    init();
    setupMutationObserver();
  } else {
    window.addEventListener('load', function() {
      init();
      setupMutationObserver();
    });
  }

  // Shopifyのセクション再読み込みにも対応
  document.addEventListener('shopify:section:load', function() {
    setTimeout(init, 100);
  });
})();
